import type {
  Notification,
  NotificationAJAXResponse,
  NotificationApprovalStatus,
  NotificationSettingsPanelData,
  NotificationsItemData,
  MiniIconStyles,
  NotificationUpdateDispatchType,
} from '@Panels/notifications-panel/notifications-panel.types';
import {NotificationIconType} from '@Panels/notifications-panel/notifications-panel.types';
import {NotificationsPanelDisplayType} from '@Panels/notifications-panel/notifications-panel.types';
import {NotificationStatus} from '@Panels/notifications-panel/notifications-panel.types';
import {
  setAllNotifications,
  setNewNotifications,
  setPanelDisplayType,
  setPreviousNotificationSettings,
  updateNewNotificationCount,
  updateNotificationsAfterStatusUpdate,
} from '@Panels/notifications-panel/notifications-panel-slice';
import {getDateFromUnixTimestamp, getTimeDifferenceFromNow} from '@Utils/date.util';
import {isValidURL2} from '@Utils/url.util';

export const AGGREGATED = 'aggregated';

export const NOTIFICATION_FETCH_LIMIT = 20;
export const HEIGHT_FROM_BOTTOM = 100;
const TOTAL_HOURS_1_DAY = 24;
const TOTAL_HOURS_2_DAYS = 48;

export function getIconStyles(notificationMiniItemType: NotificationIconType): MiniIconStyles {
  switch (notificationMiniItemType) {
    case NotificationIconType.NEUTRAL:
      return {className: 'bg-neutral-3 content-body', hoverClassName: 'bg-neutral-6'};
    case NotificationIconType.PRIMARY:
      return {className: 'bg-primary-10 content-primary', hoverClassName: 'bg-primary-10'};
    case NotificationIconType.SECONDARY:
      return {className: 'bg-secondary-10 content-secondary', hoverClassName: 'bg-secondary-10'};
    case NotificationIconType.DANGER:
      return {className: 'bg-danger-10 content-danger', hoverClassName: 'bg-danger-10'};
    case NotificationIconType.SUCCESS:
      return {className: 'bg-success-10 content-success', hoverClassName: 'bg-success-10'};
    case NotificationIconType.AI:
      return {className: 'bg-ai content-ai', hoverClassName: 'bg-ai'};
    case NotificationIconType.WARNING:
      return {className: 'bg-warning-secondary-default ct-warning-primary', hoverClassName: 'bg-warning-secondary-default'};
  }
}
export const doesUserHaveNoNotifications = (notificationAJAXData: NotificationAJAXResponse): boolean => {
  const totalLength = notificationAJAXData.new.length + notificationAJAXData.yesterday.length + notificationAJAXData.others.length + notificationAJAXData.today.length;
  return totalLength === 0;
};
export const areAllNotificationsTurnedOff = (notificationSettings: NotificationSettingsPanelData): boolean => {
  return Object.values(notificationSettings).every((setting) => setting === false);
};
export const areAllNotificationsRead = (allNotifications: Notification[]): boolean => {
  return allNotifications.every((n) => n.status === NotificationStatus.READ);
};

export const getTotalCountOfNotifications = (notificationAJAXData: NotificationAJAXResponse): number => {
  return notificationAJAXData.new.length + notificationAJAXData.yesterday.length + notificationAJAXData.others.length + notificationAJAXData.today.length;
};

export const hasNotificationSettingsChanged = (newSettings: NotificationSettingsPanelData, oldSettings: NotificationSettingsPanelData): boolean => {
  return (
    newSettings.designUpdates !== oldSettings.designUpdates ||
    newSettings.teamUpdates !== oldSettings.teamUpdates ||
    newSettings.emailCampaigns !== oldSettings.emailCampaigns ||
    newSettings.socialMediaPosts !== oldSettings.socialMediaPosts ||
    newSettings.eventsUpdates !== oldSettings.eventsUpdates
  );
};

export const bucketizeNotifications = (notifications: Notification[]): NotificationUpdateDispatchType => {
  const buckets: NotificationUpdateDispatchType = {
    today: [],
    yesterday: [],
    others: [],
  };

  notifications.forEach((n) => {
    if (!n.createdOn) {
      return;
    }

    const timeSinceCreation = getTimeDifferenceFromNow(getDateFromUnixTimestamp(n.createdOn));

    if (timeSinceCreation.intervalInHours <= TOTAL_HOURS_1_DAY) {
      buckets.today.push(n);
    } else if (timeSinceCreation.intervalInHours <= TOTAL_HOURS_2_DAYS) {
      buckets.yesterday.push(n);
    } else {
      buckets.others.push(n);
    }
  });

  return buckets;
};

export const convertNotificationsToStatus = (notifications: Notification[], status: NotificationStatus): Notification[] => {
  return notifications.map((n) => ({
    ...n,
    status: status,
  }));
};

const persistAndUpdateNotifications = (updatedNotification: Notification, updatedFilteredNotifications: Notification[], allBuckets: NotificationUpdateDispatchType): void => {
  return void window.PMW.writeLocal('notifications/markNotificationAsRead', {notificationId: updatedNotification.hashedId, itemData: updatedNotification.itemData}).then(() => {
    window.PMW.redux.store.dispatch(setAllNotifications(updatedFilteredNotifications));
    window.PMW.redux.store.dispatch(updateNotificationsAfterStatusUpdate(allBuckets));
  });
};

const updateItemDataForNotification = (clickedNotification: Notification, approvalStatus: null | NotificationApprovalStatus): Notification => {
  try {
    let itemData = JSON.parse(clickedNotification.itemData) as NotificationsItemData;
    if (approvalStatus !== null) {
      itemData = {...itemData, approvalStatus: approvalStatus};
    }
    return {
      ...clickedNotification,
      itemData: JSON.stringify(itemData),
    };
  } catch (e) {
    console.error('Error parsing itemData:', e);
  }

  return clickedNotification;
};

export const markClickedNotificationAsRead = (
  clickedNotification: Notification,
  allNotifications: Notification[],
  redirectionLink: string,
  approvalStatus: null | NotificationApprovalStatus = null
): void => {
  const updatedFilteredNotifications = allNotifications.map((n) => {
    if (n.hashedId === clickedNotification.hashedId) {
      const clickedNotification = updateItemDataForNotification(n, approvalStatus);
      return convertNotificationsToStatus([clickedNotification], NotificationStatus.READ)[0];
    } else if (n.status === NotificationStatus.NEW) {
      return convertNotificationsToStatus([n], NotificationStatus.SEEN)[0];
    }
    return n;
  });

  const updatedNotification = updateItemDataForNotification(clickedNotification, approvalStatus);
  const allBuckets = bucketizeNotifications(updatedFilteredNotifications);

  void window.PMW.writeLocal('notifications/markAllNewNotificationAsSeen').then(() => {
    persistAndUpdateNotifications(updatedNotification, updatedFilteredNotifications, allBuckets);
  });
  handleRedirectionLink(redirectionLink);
};

export const handleRedirectionLink = (redirectionLink: string): void => {
  if (redirectionLink === '') {
    return;
  }
  if (isValidURL2(redirectionLink)) {
    window.location.href = redirectionLink;
  } else {
    window.location.href = window.PMW.util.site_url(redirectionLink);
  }
};
export const updateNotificationSettings = (notificationSettings: NotificationSettingsPanelData, previousNotificationSettings: NotificationSettingsPanelData): void => {
  if (hasNotificationSettingsChanged(notificationSettings, previousNotificationSettings)) {
    void window.PMW.writeLocal('notifications/updateNotificationSettings', {
      notificationSettings,
    });
    window.PMW.redux.store.dispatch(setPreviousNotificationSettings(notificationSettings));
  }
};

export const fetchNewNotifications = (): void => {
  const allNotifications = window.PMW.redux.store.getState().notificationsPanel.allNotifications;
  const latestCreatedOn = allNotifications.length > 0 ? allNotifications[0].createdOn : null;

  void window.PMW.readLocal('notifications/getAllNewNotificationsForUser', {createdOn: latestCreatedOn}).then((response: Notification[]) => {
    if (!response?.length) return;

    const store = window.PMW.redux.store;
    const existingNewNotifications: Notification[] = store.getState().notificationsPanel.newNotifications ?? [];
    const panelDisplayType = store.getState().notificationsPanel.panelDisplayType;

    const existingIds = new Set(existingNewNotifications.map((n) => n.hashedId));
    const uniqueNewNotifications = response.filter((n) => !existingIds.has(n.hashedId));

    if (uniqueNewNotifications.length > 0) {
      store.dispatch(setNewNotifications(uniqueNewNotifications));
      store.dispatch(updateNewNotificationCount(uniqueNewNotifications.length));
      if (panelDisplayType !== NotificationsPanelDisplayType.LOADED) {
        store.dispatch(setPanelDisplayType(NotificationsPanelDisplayType.LOADED));
      }
    }
  });
};
